import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '../../supabaseClient'
import { AlertCircle, Check, Loader2 } from 'lucide-react'

/* Pagina pubblica: chi arriva dal sito vetrina ci racconta il progetto.
   Nessun account, nessuna password: l'accesso al gestionale glielo diamo noi dopo.

   Attenzione: questa pagina segue lo stile del SITO (plutonic-labs-web.pages.dev),
   non quello scuro della dashboard, perche' e' l'unica pagina che vede un cliente nuovo.
   Colori e bordi sono scritti a mano qui dentro per non toccare il tema del gestionale. */

const CATEGORIE = [
  { value: 'animazione', label: 'Animazione 2D o 3D' },
  { value: 'vfx', label: 'VFX ed effetti visivi' },
  { value: 'montaggio', label: 'Montaggio, color e finalizzazione' },
  { value: 'giochi', label: 'Giochi o esperienze VR' },
  { value: 'strumenti', label: 'Strumenti e plugin' },
  { value: 'non_so', label: 'Non lo so ancora' },
]

/* Il sito vetrina usa i nomi in inglese negli indirizzi: li traduciamo in quelli del database */
const ALIAS = { animation: 'animazione', fx: 'vfx', edit: 'montaggio', games: 'giochi', tools: 'strumenti' }

const BOZZA = 'plutonic-richiesta-bozza'

const VUOTO = {
  nome: '', cognome: '', email: '', telefono: '', azienda: '',
  categoria: 'non_so', descrizione: '', scadenza: '', budget: '', riferimenti: '',
}

const FONT_TITOLI = "font-['Rubik_Mono_One',Impact,sans-serif]"
const FONT_TESTO = "font-['Public_Sans','Segoe_UI',sans-serif]"

const labelCls = 'mb-2 block font-bold'
const inputCls =
  'w-full border-[3px] border-[#0E0418] bg-white px-4 py-3 text-base text-[#0E0418] placeholder-[#6B6478] outline-none focus:ring-4 focus:ring-[#B0126E]/30'
const errCls = 'mt-2 text-sm font-semibold text-[#B0126E]'
const optCls = 'font-normal text-[#6B6478]'

const validate = (v) => {
  const e = {}
  if (v.nome.trim().length < 2) e.nome = 'Scrivi il tuo nome'
  if (v.cognome.trim().length < 2) e.cognome = 'Scrivi il tuo cognome'
  if (!/^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(v.email.trim())) e.email = 'Controlla la mail: serve nella forma nome@dominio.it'
  if (v.telefono.replace(/\D/g, '').length < 6) e.telefono = 'Serve un numero dove richiamarti'
  if (v.descrizione.trim().length < 20) e.descrizione = 'Raccontaci qualcosa in piu: bastano due righe'
  return e
}

const etichettaCategoria = (value) => CATEGORIE.find(c => c.value === value)?.label ?? value

const Richiesta = () => {
  const [params] = useSearchParams()
  const [values, setValues] = useState(() => {
    let bozza = {}
    try { bozza = JSON.parse(localStorage.getItem(BOZZA) || '{}') } catch { bozza = {} }
    const q = (params.get('categoria') || '').toLowerCase()
    const daSito = ALIAS[q] || (CATEGORIE.some(c => c.value === q) ? q : null)
    return { ...VUOTO, ...bozza, ...(daSito ? { categoria: daSito } : {}) }
  })
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [status, setStatus] = useState('idle') // idle | sending | sent | error
  const [inviata, setInviata] = useState(null)
  const trappola = useRef(null)

  /* I caratteri del sito servono solo qui: li carichiamo quando la pagina e' aperta */
  useEffect(() => {
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = 'https://fonts.googleapis.com/css2?family=Public+Sans:wght@400;500;600;700;800&family=Rubik+Mono+One&display=swap'
    document.head.appendChild(link)
    return () => { document.head.removeChild(link) }
  }, [])

  /* Bozza salvata nel browser: se ricarica o sbaglia pagina non perde quello che ha scritto */
  useEffect(() => {
    try { localStorage.setItem(BOZZA, JSON.stringify(values)) } catch { /* navigazione privata */ }
  }, [values])

  const set = (campo) => (e) => setValues(v => ({ ...v, [campo]: e.target.value }))
  const blur = (campo) => () => {
    setTouched(t => ({ ...t, [campo]: true }))
    setErrors(validate(values))
  }
  const erroreDi = (campo) => (touched[campo] ? errors[campo] : null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    const err = validate(values)
    setErrors(err)
    setTouched({ nome: true, cognome: true, email: true, telefono: true, descrizione: true })
    const primo = Object.keys(err)[0]
    if (primo) {
      document.getElementById(primo)?.focus()
      return
    }
    /* campo trappola: se e' compilato e' un bot, facciamo finta di niente */
    if (trappola.current?.value) { setInviata(values); setStatus('sent'); return }

    setStatus('sending')
    const { error } = await supabase.from('richieste').insert({
      nome: values.nome.trim(),
      cognome: values.cognome.trim(),
      email: values.email.trim(),
      telefono: values.telefono.trim(),
      azienda: values.azienda.trim() || null,
      categoria: values.categoria,
      descrizione: values.descrizione.trim(),
      scadenza: values.scadenza || null,
      budget: values.budget.trim() || null,
      riferimenti: values.riferimenti.trim() || null,
      origine: (params.get('da') || 'sito').slice(0, 40),
    })
    if (error) { setStatus('error'); return }
    setInviata(values)
    try { localStorage.removeItem(BOZZA) } catch { /* ignora */ }
    setStatus('sent')
  }

  const Intestazione = ({ titolo, sottotitolo }) => (
    <header className="border-b-[3px] border-[#0E0418] bg-[#12071F] text-[#FBF4FF]">
      <div className="mx-auto w-full max-w-2xl px-5 py-10 sm:px-8 sm:py-14">
        <img
          src="/logo-plutonic.png"
          alt="Plutonic Labs"
          className="w-36 drop-shadow-[0_0_40px_rgba(168,85,247,0.55)] sm:w-48"
        />
        <h1 className={`${FONT_TITOLI} mt-8 text-xl leading-snug sm:text-3xl`}>{titolo}</h1>
        {sottotitolo && <p className="mt-4 text-lg leading-relaxed text-[#D8C8EA]">{sottotitolo}</p>}
      </div>
    </header>
  )

  if (status === 'sent' && inviata) {
    return (
      <div className={`${FONT_TESTO} min-h-screen bg-[#F3F0F7] text-[#0E0418]`}>
        <Intestazione
          titolo="Richiesta inviata"
          sottotitolo="La legge una persona del team, non un sistema automatico."
        />
        <main className="mx-auto w-full max-w-2xl px-5 py-10 sm:px-8 sm:py-14">
          <div className="border-[3px] border-[#0E0418] bg-white p-6 shadow-[8px_8px_0_#0E0418] sm:p-8">
            <div className="flex h-12 w-12 items-center justify-center border-[3px] border-[#0E0418] bg-[#F4F07C]">
              <Check size={24} />
            </div>
            <p className="mt-5 text-lg leading-relaxed">
              Ti rispondiamo entro due giorni lavorativi a <span className="font-bold break-words">{inviata.email}</span>,
              e se serve ti chiamiamo al {inviata.telefono}.
            </p>
            <p className="mt-3 leading-relaxed text-[#4B4458]">
              Hai altro materiale da mandarci, come riferimenti o girato? Rispondi alla nostra mail e lo
              alleghiamo alla richiesta.
            </p>

            <div className="mt-6 border-t-[3px] border-[#0E0418] pt-6">
              <p className={`${FONT_TITOLI} text-xs`}>Riepilogo</p>
              <dl className="mt-4 space-y-3 text-[15px]">
                <div className="flex gap-3">
                  <dt className="w-28 flex-shrink-0 text-[#6B6478]">Nome</dt>
                  <dd className="min-w-0 break-words font-semibold">
                    {inviata.nome} {inviata.cognome}{inviata.azienda ? ' - ' + inviata.azienda : ''}
                  </dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-28 flex-shrink-0 text-[#6B6478]">Tipo di lavoro</dt>
                  <dd className="min-w-0 break-words">{etichettaCategoria(inviata.categoria)}</dd>
                </div>
                {inviata.scadenza && (
                  <div className="flex gap-3">
                    <dt className="w-28 flex-shrink-0 text-[#6B6478]">Scadenza</dt>
                    <dd className="min-w-0 break-words">{inviata.scadenza}</dd>
                  </div>
                )}
                {inviata.budget && (
                  <div className="flex gap-3">
                    <dt className="w-28 flex-shrink-0 text-[#6B6478]">Budget</dt>
                    <dd className="min-w-0 break-words">{inviata.budget}</dd>
                  </div>
                )}
                <div className="flex gap-3">
                  <dt className="w-28 flex-shrink-0 text-[#6B6478]">Progetto</dt>
                  <dd className="min-w-0 whitespace-pre-line break-words">{inviata.descrizione}</dd>
                </div>
              </dl>
            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className={`${FONT_TESTO} min-h-screen bg-[#F3F0F7] text-[#0E0418]`}>
      <Intestazione titolo="Raccontaci il progetto" />

      <main className="mx-auto w-full max-w-2xl px-5 py-10 sm:px-8 sm:py-14">
        <form onSubmit={handleSubmit} noValidate className="space-y-6">
          {/* campo trappola per i bot: invisibile, nessuna persona lo compila */}
          <input ref={trappola} type="text" name="sito_web" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />

          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="nome" className={labelCls}>Nome</label>
              <input id="nome" name="nome" autoComplete="given-name" value={values.nome}
                onChange={set('nome')} onBlur={blur('nome')} className={inputCls} />
              {erroreDi('nome') && <p className={errCls}>{erroreDi('nome')}</p>}
            </div>
            <div>
              <label htmlFor="cognome" className={labelCls}>Cognome</label>
              <input id="cognome" name="cognome" autoComplete="family-name" value={values.cognome}
                onChange={set('cognome')} onBlur={blur('cognome')} className={inputCls} />
              {erroreDi('cognome') && <p className={errCls}>{erroreDi('cognome')}</p>}
            </div>
            <div>
              <label htmlFor="email" className={labelCls}>Email</label>
              <input id="email" name="email" type="email" inputMode="email" autoComplete="email" value={values.email}
                onChange={set('email')} onBlur={blur('email')} className={inputCls} />
              {erroreDi('email') && <p className={errCls}>{erroreDi('email')}</p>}
            </div>
            <div>
              <label htmlFor="telefono" className={labelCls}>Telefono</label>
              <input id="telefono" name="telefono" type="tel" inputMode="tel" autoComplete="tel" value={values.telefono}
                onChange={set('telefono')} onBlur={blur('telefono')} className={inputCls} />
              {erroreDi('telefono') && <p className={errCls}>{erroreDi('telefono')}</p>}
            </div>
          </div>

          <div>
            <label htmlFor="azienda" className={labelCls}>
              Azienda <span className={optCls}>- facoltativo</span>
            </label>
            <input id="azienda" name="azienda" autoComplete="organization" value={values.azienda}
              onChange={set('azienda')} className={inputCls} />
          </div>

          <div>
            <label htmlFor="categoria" className={labelCls}>Di cosa hai bisogno</label>
            <select id="categoria" name="categoria" value={values.categoria} onChange={set('categoria')} className={inputCls}>
              {CATEGORIE.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>

          <div>
            <label htmlFor="descrizione" className={labelCls}>Il progetto</label>
            <textarea id="descrizione" name="descrizione" rows={6} value={values.descrizione}
              onChange={set('descrizione')} onBlur={blur('descrizione')} className={inputCls}
              placeholder="Cosa ti serve, per quando, e se hai dei riferimenti da mostrarci" />
            {erroreDi('descrizione') && <p className={errCls}>{erroreDi('descrizione')}</p>}
          </div>

          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="scadenza" className={labelCls}>
                Scadenza <span className={optCls}>- facoltativo</span>
              </label>
              <input id="scadenza" name="scadenza" type="date" value={values.scadenza}
                onChange={set('scadenza')} className={inputCls} />
            </div>
            <div>
              <label htmlFor="budget" className={labelCls}>
                Budget indicativo <span className={optCls}>- facoltativo</span>
              </label>
              <input id="budget" name="budget" value={values.budget} onChange={set('budget')} className={inputCls}
                placeholder="va bene anche una fascia: 2-3k" />
              <p className="mt-2 text-sm text-[#6B6478]">Ci serve per capire subito se possiamo aiutarti.</p>
            </div>
          </div>

          <div>
            <label htmlFor="riferimenti" className={labelCls}>
              Link di riferimento <span className={optCls}>- facoltativo</span>
            </label>
            <textarea id="riferimenti" name="riferimenti" rows={2} value={values.riferimenti}
              onChange={set('riferimenti')} className={inputCls}
              placeholder="Video, moodboard, sito: incolla qui gli indirizzi" />
          </div>

          {status === 'error' && (
            <div role="alert" className="flex gap-3 border-[3px] border-[#0E0418] bg-[#EE3A5B] p-4 text-[#FBF4FF]">
              <AlertCircle size={20} className="mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-bold">La richiesta non e partita</p>
                <p className="mt-1 text-sm">Quello che hai scritto resta qui: riprova tra un minuto.</p>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'sending'}
            className={`${FONT_TITOLI} flex min-h-[56px] w-full items-center justify-center gap-2 border-[3px] border-[#0E0418] bg-gradient-to-r from-[#F4F07C] via-[#F7A541] to-[#EE3A5B] px-6 text-xs shadow-[6px_6px_0_#0E0418] transition-transform duration-150 hover:-translate-y-0.5 active:translate-y-0.5 disabled:opacity-60 sm:text-sm`}
          >
            {status === 'sending'
              ? <><Loader2 size={18} className="animate-spin" /> Invio in corso...</>
              : 'Invia la richiesta'}
          </button>

          <p className="text-center text-sm text-[#4B4458]">
            Usiamo nome, email e telefono solo per risponderti, come spiegato nell&apos;
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="font-bold underline underline-offset-2">
              informativa privacy
            </a>
            . Nessun account da creare: se partiamo, l&apos;accesso all&apos;area clienti te lo mandiamo noi.
          </p>
        </form>
      </main>
    </div>
  )
}

export default Richiesta
